import React from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import escapeRegExp from "lodash/escapeRegExp";

import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Select, { SelectOption } from "@amzn/meridian/select";
import Loader from "@amzn/meridian/loader";
import Theme from "@amzn/meridian/theme";
import Modal from "@amzn/meridian/modal";
import orangeLightTokens from "@amzn/meridian-tokens/theme/orange-light";

import { keys } from "i18n";
import { LOCATION_TYPES, EDIT_YARD_MAP_TYPES } from "app-constants";
import { validatePolygonForLocationType } from "utils/geoJsonUtils";
import SearchField, { SearchSuggestion } from "@amzn/meridian/search-field";

class LocationMappingInfo extends React.PureComponent {
  state = {
    isEditButtonClicked: false,
    showDeleteConfirmation: false,
  };

  componentDidUpdate(prevProps) {
    if (this.props.selectedLocation && prevProps.selectedLocation !== this.props.selectedLocation) {
      this.initLocationDetails();
    }
  }

  toggleEditCancelButtons = () => {
    if (this.state.isEditButtonClicked) {
      this.initLocationDetails();
    } else {
      this.setState({ isEditButtonClicked: true });
    }
  };

  initLocationDetails = () => {
    const { yardMapData, selectedLocation, clearErrorEditingYardMap } = this.props;
    this.setState(
      {
        isEditButtonClicked: false,
        locationCode: selectedLocation.locationCode,
        locationType: selectedLocation.locationType,
        validationError: null, // clear validation error
      },
      () => yardMapData.errorEditingYardMap && clearErrorEditingYardMap()
    );
  };

  setLocationCode = (query) => {
    this.setState({
      locationCode: query,
      validationError: null,
    });
  };

  filterSuggestions = (query, suggestions) => {
    const searchRegExp = new RegExp(escapeRegExp(query), "i");
    return suggestions.filter((suggestion) => !query || searchRegExp.test(suggestion.label));
  };

  getFilteredLocationCodes = () => {
    const { locationCode } = this.state;
    const availableLocations = this.getAvailableLocationCodes();
    return this.filterSuggestions(locationCode, availableLocations);
  };

  getAvailableLocationCodes = () => {
    const { locations } = this.props;
    return locations.sort((a, b) => a.label.localeCompare(b.label));
  };

  setLocationType = (locationType) => {
    this.setState({
      locationType,
      validationError: null, // clear validation error when location type changes
    });
  };

  showDeleteConfirmation = () => this.setState({ showDeleteConfirmation: true });

  hideDeleteConfirmation = () => this.setState({ showDeleteConfirmation: false });

  confirmDeleteLocationMapping = () => {
    this.deleteLocationMapping();
    this.hideDeleteConfirmation();
  };

  saveLocationDetails = () => {
    const { selectedLocation, yardMapData, editYardMap, autoSelectLocationWithChangedCode, t } =
      this.props;
    const { locationCode, locationType } = this.state;

    // validate location code format and duplication check
    if (locationCode.length > 100) {
      this.setState({ validationError: `${t(keys.LOCATION_CODE_ERROR_MAX_LENGTH)}` });
      return;
    }

    const isLocationCodeChanged = locationCode !== selectedLocation.locationCode;
    if (isLocationCodeChanged) {
      const existingLocation = yardMapData.yardMap.locations.find(
        (loc) => loc.locationCode === locationCode
      );
      if (existingLocation) {
        this.setState({
          validationError: `${t(keys.GEOJSON_ERROR_DUPLICATE_LOCATION_CODES)}: ${locationCode}`,
        });
        return;
      }
    }

    // validate location type change using shared validation logic
    try {
      const polygon = selectedLocation.ydlrPolygon || selectedLocation.polygon;
      validatePolygonForLocationType(polygon, locationType);
    } catch (error) {
      // set validation error to show user-friendly message
      this.setState({ validationError: error.message });
      return;
    }

    // clear any previous validation errors
    this.setState({ validationError: null });

    const { yardId, version } = yardMapData.yardMap;
    const deleteLocationCodes = isLocationCodeChanged ? [selectedLocation.locationCode] : [];
    const createOrUpdateLocations = this.getCreateOrUpdateLocations();

    editYardMap(
      {
        editType: EDIT_YARD_MAP_TYPES.SAVE_LOCATION_DETAILS,
        yardId,
        version,
        deleteLocationCodes,
        createOrUpdateLocations,
      },
      () => isLocationCodeChanged && autoSelectLocationWithChangedCode(locationCode)
    );
  };

  deleteLocationMapping = () => {
    const { yardMapData, selectedLocation, editYardMap } = this.props;
    const { yardId, version } = yardMapData.yardMap;
    const deleteLocationCodes = [selectedLocation.locationCode];

    editYardMap({
      editType: EDIT_YARD_MAP_TYPES.DELETE_LOCATION_MAPPING,
      yardId,
      version,
      deleteLocationCodes,
    });
  };

  getCreateOrUpdateLocations = () => {
    const { selectedLocation } = this.props;
    const { locationCode, locationType } = this.state;
    return [
      {
        locationCode,
        locationType,
        // ydlrPolygon when it's selected from YardMap, polygon when it's refreshed from redux
        // Regardless, it's back-end Cartesian coordinates
        polygon: selectedLocation.ydlrPolygon || selectedLocation.polygon,
        parkingDirection: selectedLocation.parkingDirection,
        needsRemapping: selectedLocation.needsRemapping,
      },
    ];
  };

  areLocationDetailsChanged = () => {
    const { selectedLocation } = this.props;
    const { locationCode, locationType } = this.state;
    return (
      locationCode !== selectedLocation.locationCode ||
      locationType !== selectedLocation.locationType
    );
  };

  isLocationCodeNonEmpty = () => {
    const { locationCode } = this.state;
    return !!(locationCode && locationCode.trim().length > 0);
  };

  areLocationDetailsChangedAndNonEmpty = () => {
    return this.areLocationDetailsChanged() && this.isLocationCodeNonEmpty();
  };

  render() {
    const { t, dialogHeight, yardMapData, selectedLocation, permissionsData } = this.props;
    const {
      isEditButtonClicked,
      locationCode,
      locationType,
      showDeleteConfirmation,
      validationError,
    } = this.state;

    const canEdit = !!(
      permissionsData &&
      permissionsData.permissions &&
      permissionsData.permissions.canEdit
    );

    const content = selectedLocation ? (
      <Column
        height={dialogHeight}
        maxHeight={dialogHeight}
        overflowY="scroll"
        alignmentVertical="justify"
        spacingInset="450"
      >
        <Column>
          <Row alignmentHorizontal="justify">
            <Text type="b500">{t(keys.LOCATION_DETAILS)}</Text>
            {canEdit && (
              <Button type="link" onClick={this.toggleEditCancelButtons}>
                {isEditButtonClicked ? t(keys.CANCEL) : t(keys.EDIT)}
              </Button>
            )}
          </Row>

          <Column spacing="small">
            <Text type="h100">{t(keys.LOCATION_CODE)}</Text>
            {isEditButtonClicked ? (
              <SearchField
                popoverMaxHeight={500}
                value={locationCode}
                onChange={this.setLocationCode}
                placeholder={t(keys.LOCATION_CODE_PLACEHOLDER)}
              >
                {this.getFilteredLocationCodes().map(({ label }) => (
                  <SearchSuggestion key={label} onClick={() => this.setLocationCode(label)}>
                    {label}
                  </SearchSuggestion>
                ))}
              </SearchField>
            ) : (
              <Text>{selectedLocation.locationCode}</Text>
            )}
          </Column>

          <Column spacing="small">
            <Text type="h100">{t(keys.LOCATION_TYPE)}</Text>
            {isEditButtonClicked ? (
              <Select value={locationType} onChange={this.setLocationType}>
                {LOCATION_TYPES.map((locationType) => (
                  <SelectOption key={locationType} value={locationType} label={t(locationType)} />
                ))}
              </Select>
            ) : (
              <Text>{t(selectedLocation.locationType)}</Text>
            )}
          </Column>

          {yardMapData.errorEditingYardMap && (
            <>
              <Text color="error">{t(keys.EDIT_YARD_MAP_ERROR_MESSAGE)}</Text>
              <Text color="error" breakWord>
                {yardMapData.errorEditingYardMap}
              </Text>
            </>
          )}

          {validationError && (
            <Text color="error" breakWord>
              {validationError}
            </Text>
          )}
        </Column>

        {isEditButtonClicked && (
          <Column spacing="small">
            <Theme tokens={orangeLightTokens}>
              {yardMapData.editingYardMap.deleteLocationMapping ? (
                <Row alignmentHorizontal="center">
                  <Loader size="medium" />
                </Row>
              ) : (
                <Button type="secondary" onClick={this.showDeleteConfirmation}>
                  {t(keys.DELETE_LOCATION_MAPPING)}
                </Button>
              )}
            </Theme>

            {yardMapData.editingYardMap.saveLocationDetails ? (
              <Row alignmentHorizontal="center">
                <Loader size="medium" />
              </Row>
            ) : (
              <Button
                type="primary"
                disabled={!this.areLocationDetailsChangedAndNonEmpty()}
                onClick={this.saveLocationDetails}
              >
                {t(keys.SAVE_LOCATION_DETAILS)}
              </Button>
            )}
          </Column>
        )}
      </Column>
    ) : (
      <Column spacing="small" spacingInset="450">
        {yardMapData.fetchingYardMap ? (
          <Text>{t(keys.FETCHING_YARD_MAP)}</Text>
        ) : yardMapData.errorFetchingYardMap ? (
          <>
            <Text color="error">{t(keys.FETCH_YARD_MAP_ERROR_MESSAGE)}</Text>
            <Text color="error" breakWord>
              {yardMapData.errorFetchingYardMap}
            </Text>
          </>
        ) : (
          <>
            <Text>
              {yardMapData.yardMap.locations?.length} {t(keys.LOCATION_MAPPING_COUNT_TEXT)}
            </Text>
            <Text>{t(keys.SELECT_LOCATION_INSTRUCTION)}</Text>
          </>
        )}
      </Column>
    );

    return (
      <>
        {content}
        {showDeleteConfirmation && (
          <Modal
            width="400px"
            bodySpacingInset="medium"
            title={t(keys.DELETE_LOCATION_MAPPING)}
            open={true}
          >
            <Text>
              {t(keys.DELETE_LOCATION_MAPPING_CONFIRMATION, {
                locationCode: selectedLocation?.locationCode,
              })}
            </Text>

            <Row alignmentHorizontal="right" spacingInset="large none none none">
              <Button type="secondary" minWidth="80px" onClick={this.hideDeleteConfirmation}>
                {t(keys.CANCEL)}
              </Button>

              <Button type="primary" minWidth="90px" onClick={this.confirmDeleteLocationMapping}>
                {t(keys.DELETE)}
              </Button>
            </Row>
          </Modal>
        )}
      </>
    );
  }
}

export default withTranslation()(LocationMappingInfo);

LocationMappingInfo.propTypes = {
  dialogHeight: PropTypes.number.isRequired,
  yardMapData: PropTypes.object.isRequired,
  selectedLocation: PropTypes.object,
  locations: PropTypes.arrayOf(PropTypes.object).isRequired,
  editYardMap: PropTypes.func.isRequired,
  autoSelectLocationWithChangedCode: PropTypes.func.isRequired,
  clearErrorEditingYardMap: PropTypes.func.isRequired,
  permissionsData: PropTypes.object,
};
